import React, { useEffect, useCallback } from 'react'; // Ensure useCallback is imported

const AspectRatioControls = () => {

  let aspectRatioValue = 'None';

  const updateCanvasSize = useCallback(() => {
    const canvas = document.getElementById('aspect-ratio-canvas');
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;

    // Define the top and bottom margins
    const marginTop = 51;
    const marginBottom = 87;

    // Adjust the canvas size to fit within the margins
    const canvasHeight = window.innerHeight - marginTop - marginBottom;
    const canvasWidth = window.innerWidth;

    // Set canvas style and actual dimensions
    canvas.style.position = 'absolute';
    canvas.style.top = `${marginTop}px`;
    canvas.style.left = '0';
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    canvas.width = canvasWidth * pixelRatio;
    canvas.height = canvasHeight * pixelRatio;
  }, []);

  const drawAspectRatioRect = useCallback((aspectRatioString) => {
    updateCanvasSize(); // Update canvas size before drawing
    const canvas = document.getElementById('aspect-ratio-canvas');
    if (!canvas) return;

    aspectRatioValue = aspectRatioString;

    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;

    // Update the canvas resolution based on the device's pixel ratio
    canvas.width = canvas.offsetWidth * pixelRatio;
    canvas.height = canvas.offsetHeight * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    // Clear the previous drawing
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (aspectRatioString === 'None') {
      return;
    }

    const [widthRatio, heightRatio] = aspectRatioString.split(':').map(Number);
    const aspectRatio = widthRatio / heightRatio;

    // Define a safe area margin in pixels
    const margin = 0.5;

    // Calculate the safe drawing area within the canvas
    const safeWidth = canvas.width / pixelRatio - margin * 2;
    const safeHeight = canvas.height / pixelRatio - margin * 2;

    let rectWidth, rectHeight;
    if (safeWidth / safeHeight > aspectRatio) {
      rectHeight = safeHeight;
      rectWidth = rectHeight * aspectRatio;
    } else {
      rectWidth = safeWidth;
      rectHeight = rectWidth / aspectRatio;
    }

    const x = (canvas.width / pixelRatio - rectWidth) / 2;
    const y = (canvas.height / pixelRatio - rectHeight) / 2;

    // Draw the black overlay outside the aspect ratio rectangle
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.beginPath();

    // Top overlay
    ctx.fillRect(0, 0, canvas.width / pixelRatio, y);

    // Bottom overlay
    ctx.fillRect(0, y + rectHeight, canvas.width / pixelRatio, canvas.height / pixelRatio - (y + rectHeight));

    // Left overlay
    ctx.fillRect(0, y, x, rectHeight);

    // Right overlay
    ctx.fillRect(x + rectWidth, y, canvas.width / pixelRatio - (x + rectWidth), rectHeight);

    // Draw the red border for the aspect ratio rectangle
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, rectWidth, rectHeight);
  }, []);


  useEffect(() => {
    // Function to handle the resize event
    const handleResize = () => {
      drawAspectRatioRect(aspectRatioValue);
    };

    const handleAspectRatioChange = (event) => {
      drawAspectRatioRect(event.detail.activeRatio);
    };

    // Add the event listeners for resize and aspect ratio change
    window.addEventListener('resize', handleResize);
    window.addEventListener('aspectRatioChange', handleAspectRatioChange);

    // Call the drawing function initially to draw the aspect ratio rectangle
    handleResize();

    // Leaving the scene: the canvas is in index.html, outside the scene page,
    // so clear the frame too — it stayed over the Captures page.
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('aspectRatioChange', handleAspectRatioChange);
      drawAspectRatioRect('None');
    };
  }, [], drawAspectRatioRect);
};

export default AspectRatioControls;
